import React, { useRef } from "react";
import { CheckCircle, XCircle, Star, Info, X } from "lucide-react";
import { RateAnalysis, QuoteRequest } from "../types/rate-analysis";
import SpeakToProfessionalModal from "./SpeakToProfessionalModal";
import { calculateTotalMonthlyPayment } from "../utils/mortgageCalculations";
import { Tooltip } from "react-tooltip";
import { formatCurrency, formatRate } from "../utils/numberFormatting";
import { isPurchaseRequest } from "../utils/quoteRequestUtils";
import { MONTHLY_PAYMENT_TOOLTIP } from "../constants/tooltips";

interface QuoteComparisonReportProps {
  analysis: RateAnalysis;
  quoteRequest: QuoteRequest | undefined;
  isExample?: boolean;
  gradeTitle?: string;
}

interface DisplayQuote {
  rate: number;
  lenderFees: number;
  mortgageInsurance: number;
  isUserQuote: boolean;
  isRecommended: boolean;
  lenderName: string;
  monthlyPayment: number;
  apr?: number;
}

interface LoanInformationModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteRequest: QuoteRequest | undefined;
}

const LoanInformationModal: React.FC<LoanInformationModalProps> = ({
  isOpen,
  onClose,
  quoteRequest,
}) => {
  if (!isOpen || !quoteRequest) return null;

  const info = [
    {
      label: "Loan Type",
      value: isPurchaseRequest(quoteRequest) ? "Purchase" : "Refinance",
    },
    { label: "Property Type", value: quoteRequest.property_type },
    { label: "Property Style", value: quoteRequest.property_style },
    {
      label: "Military Service",
      value: quoteRequest.military_service ? "Yes" : "No",
    },
    { label: "Credit Score", value: quoteRequest.credit_score },
    { label: "Zip Code", value: quoteRequest.zip_code },
  ];

  if (isPurchaseRequest(quoteRequest)) {
    info.push(
      {
        label: "Purchase Price",
        value: `$${quoteRequest.purchase_price?.toLocaleString()}`,
      },
      {
        label: "Down Payment",
        value: `$${quoteRequest.down_payment?.toLocaleString()}`,
      },
      { label: "First Home", value: quoteRequest.is_first_home ? "Yes" : "No" }
    );
  } else {
    info.push(
      {
        label: "Remaining Balance",
        value: `$${quoteRequest.remaining_balance?.toLocaleString()}`,
      },
      { label: "Cash Out", value: quoteRequest.cash_out ? "Yes" : "No" },
      ...(quoteRequest.cash_out
        ? [
            {
              label: "Cash Out Amount",
              value: `$${quoteRequest.cash_out_amount?.toLocaleString()}`,
            },
          ]
        : [])
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 animate-fade-in">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Loan Information
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-3">
          {info.map((item, index) => (
            <div
              key={index}
              className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
            >
              <span className="text-gray-600 font-medium">{item.label}</span>
              <span className="text-gray-900">{item.value}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const QuoteComparisonReport: React.FC<QuoteComparisonReportProps> = ({
  analysis,
  quoteRequest,
  isExample = false,
  gradeTitle = "Quote Grade",
}) => {
  const [selectedQuote, setSelectedQuote] = React.useState<DisplayQuote | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isLoanInfoModalOpen, setIsLoanInfoModalOpen] = React.useState(false);
  const [selectedPartnerIndex, setSelectedPartnerIndex] = React.useState<
    number | null
  >(null);
  const openedByButtonRef = useRef(false);
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(
    null
  );

  if (!quoteRequest) {
    return null;
  }

  // Determine if any partner quote has APR
  const showAprColumn = quoteRequest.quotes?.some(
    (q) => typeof q.apr === "number" && !isNaN(q.apr)
  );

  const hasBrokerRate = !!quoteRequest.broker_rate;
  const hasProsAndCons =
    hasBrokerRate && analysis.pros?.length > 0 && analysis.cons?.length > 0;
  const hasRates = quoteRequest.broker_rate || quoteRequest.quotes?.length > 0;
  const hasRecommendations =
    analysis.recommendations && analysis.recommendations.length > 0;

  // Calculate loan amount
  const loanAmount =
    quoteRequest.loan_type === "purchase"
      ? (quoteRequest.purchase_price || 0) - (quoteRequest.down_payment || 0)
      : quoteRequest.remaining_balance || 0;

  const userQuoteGroup: DisplayQuote[] = quoteRequest.broker_rate
    ? [
        {
          rate: quoteRequest.broker_rate,
          lenderFees: quoteRequest.broker_lender_fees || 0,
          mortgageInsurance: quoteRequest.broker_mortgage_insurance || 0,
          isUserQuote: true,
          isRecommended: false,
          lenderName: isExample
            ? "Quote from the Billboard Guy"
            : "Quote You Provided",
          monthlyPayment: calculateTotalMonthlyPayment(
            loanAmount,
            quoteRequest.broker_rate,
            360, // Assuming 30-year term
            quoteRequest.broker_mortgage_insurance || 0
          ),
        },
      ]
    : [];

  const partnerQuotesGroup: DisplayQuote[] = quoteRequest.quotes?.length
    ? [...quoteRequest.quotes]
        .sort((a, b) => a.rate - b.rate)
        .map((quote) => ({
          rate: quote.rate,
          lenderFees: quote.lenderFees,
          mortgageInsurance: quote.mortgageInsurance || 0,
          isUserQuote: false,
          isRecommended: quote.isRecommended || false,
          lenderName: "Keystone Financial Services, LLC",
          monthlyPayment: calculateTotalMonthlyPayment(
            loanAmount,
            quote.rate,
            360, // Assuming 30-year term
            quote.mortgageInsurance || 0
          ),
          apr: quote.apr,
        }))
    : [];

  const hasMortgageInsurance =
    userQuoteGroup.some((q) => q.mortgageInsurance > 0) ||
    partnerQuotesGroup.some((q) => q.mortgageInsurance > 0);

  const handleSelectQuote = (quote: DisplayQuote) => {
    setSelectedQuote(quote);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedQuote(null);
    setSelectedPartnerIndex(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
      {/* Grade Display - Only show if there's a broker rate */}
      {hasBrokerRate && analysis.grade && (
        <div className="text-center mb-6">
          <div className="text-lg font-semibold text-gray-900 mb-2">
            {gradeTitle}
          </div>
          <div
            className={`
            inline-block px-6 py-2 rounded-full text-white text-4xl font-bold
            ${analysis.grade === "A" ? "bg-green-600" : ""}
            ${
              analysis.grade === "B" || analysis.grade === "C"
                ? "bg-yellow-500"
                : ""
            }
            ${
              analysis.grade === "D" || analysis.grade === "F"
                ? "bg-red-600"
                : ""
            }
          `}
          >
            {analysis.grade}
          </div>
        </div>
      )}

      {/* Overall Recommendation */}
      {hasRecommendations && (
        <div className="bg-primary-light/10 rounded-lg p-6 mb-6">
          <div className="flex items-start space-x-3">
            <Info className="h-6 w-6 text-primary mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Overall Recommendation
              </h3>
              <p className="text-gray-700">{analysis.recommendations?.[0]}</p>
            </div>
          </div>
        </div>
      )}

      {/* Mortgage Quotes Comparison */}
      {hasRates && (
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Mortgage Quotes Comparison
            </h3>
            <button
              onClick={() => setIsLoanInfoModalOpen(true)}
              className="btn-tertiary"
            >
              View Loan Scenario
            </button>
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-6">
            {/* User Quote */}
            {userQuoteGroup.length > 0 && (
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-900">
                    {userQuoteGroup[0].lenderName}
                    {quoteRequest.broker_rate && (
                      <span className="ml-2 inline-flex items-center pt-2">
                        <span className="text-sm text-gray-500 ">Grade:</span>
                        <span
                          className={`
                        ml-2 px-2 py-0.5 rounded-full text-white text-sm font-semibold
                        ${analysis.grade === "A" ? "bg-green-600" : ""}
                        ${
                          analysis.grade === "B" || analysis.grade === "C"
                            ? "bg-yellow-500"
                            : ""
                        }
                        ${
                          analysis.grade === "D" || analysis.grade === "F"
                            ? "bg-red-600"
                            : ""
                        }
                      `}
                        >
                          {analysis.grade}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-3 pl-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500 w-24">
                      Rate:
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900">
                        {formatRate(userQuoteGroup[0].rate)}%
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500 w-24">
                      Lender Fees:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700">
                        {(() => {
                          const fees = quoteRequest.broker_lender_fees;
                          if (fees === null || fees === undefined) {
                            return (
                              <span className="text-gray-500">
                                Not provided
                              </span>
                            );
                          }
                          const numericFees =
                            typeof fees === "string" ? parseFloat(fees) : fees;
                          return numericFees < 0 ? (
                            <span className="text-green-500">
                              -${formatCurrency(Math.abs(numericFees))}
                            </span>
                          ) : (
                            <span>${formatCurrency(numericFees)}</span>
                          );
                        })()}
                      </span>
                      <Info
                        size={14}
                        className="text-gray-400 cursor-help"
                        data-tooltip-id="lender-fees-tooltip-mobile"
                        data-tooltip-content="Title fees and other third-party cost estimates will be not shown in this comparison because they do not vary based on the lender you choose."
                      />
                    </div>
                  </div>
                  {hasMortgageInsurance && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24">
                        Mortgage Insurance:
                      </span>
                      <span className="text-gray-700">
                        {(() => {
                          const pmi = quoteRequest.broker_mortgage_insurance;
                          const isPurchase =
                            quoteRequest.loan_type === "purchase";
                          const downPaymentPercent =
                            isPurchase &&
                            quoteRequest.purchase_price &&
                            quoteRequest.down_payment
                              ? (quoteRequest.down_payment /
                                  quoteRequest.purchase_price) *
                                100
                              : null;

                          if (pmi === null || pmi === undefined) {
                            if (
                              isPurchase &&
                              downPaymentPercent !== null &&
                              downPaymentPercent < 20
                            ) {
                              return (
                                <span className="text-gray-500">
                                  Not provided
                                </span>
                              );
                            }
                            return <span>$0</span>;
                          }

                          return <span>${formatCurrency(pmi)}</span>;
                        })()}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500 w-24">
                      Monthly Payment:
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700">
                        $
                        {formatCurrency(
                          typeof userQuoteGroup[0].monthlyPayment === "number"
                            ? userQuoteGroup[0].monthlyPayment
                            : 0
                        )}
                      </span>
                      <Info
                        size={14}
                        className="text-gray-400 cursor-help"
                        data-tooltip-id="monthly-payment-tooltip-mobile"
                        data-tooltip-content={MONTHLY_PAYMENT_TOOLTIP}
                      />
                    </div>
                  </div>
                  {/* Don't show the APR on user quote on mobile, since we don't ask them for it */}
                </div>
              </div>
            )}

            {/* Partner Quotes Group */}
            {partnerQuotesGroup.length > 0 && (
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-900">
                    {partnerQuotesGroup[0].lenderName}
                  </div>
                  <div className="text-xs text-gray-500">NMLS #150818</div>
                </div>
                {partnerQuotesGroup.map((quote, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-4 shadow-sm"
                  >
                    <div className="space-y-3 pl-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500 w-24">
                          Rate:
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-semibold text-gray-900 cursor-pointer">
                            {formatRate(quote.rate)}%
                          </span>
                          {quote.isRecommended && (
                            <span className="ml-1">
                              <Star size={16} className="text-primary inline" />
                              <span className="sr-only">
                                Recommended Option
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500 w-24">
                          Lender Fees:
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-700">
                            {(() => {
                              const fees =
                                typeof quote.lenderFees === "string"
                                  ? parseFloat(quote.lenderFees)
                                  : quote.lenderFees;
                              return fees < 0 ? (
                                <span className="text-green-500">
                                  -${formatCurrency(Math.abs(fees))}
                                </span>
                              ) : (
                                <span>${formatCurrency(fees)}</span>
                              );
                            })()}
                          </span>
                          <Info
                            size={14}
                            className="text-gray-400 cursor-help"
                            data-tooltip-id="lender-fees-tooltip-mobile"
                            data-tooltip-content="Title fees will be included as part of your closing costs, but are not shown in this comparison because they do not vary based on the lender or broker you choose."
                          />
                        </div>
                      </div>
                      {hasMortgageInsurance && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-500 w-24">
                            Mortgage Insurance:
                          </span>
                          <span className="text-gray-700">
                            ${formatCurrency(quote.mortgageInsurance)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500 w-24">
                          Monthly Payment:
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-700">
                            $
                            {formatCurrency(
                              typeof quote.monthlyPayment === "number"
                                ? quote.monthlyPayment
                                : 0
                            )}
                          </span>
                          <Info
                            size={14}
                            className="text-gray-400 cursor-help"
                            data-tooltip-id="monthly-payment-tooltip-mobile"
                            data-tooltip-content={MONTHLY_PAYMENT_TOOLTIP}
                          />
                        </div>
                      </div>
                      {showAprColumn &&
                        typeof quote.apr === "number" &&
                        !isNaN(quote.apr) && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-500 w-24">
                              APR:
                            </span>
                            <span className="text-lg font-semibold text-gray-900 flex items-center gap-1">
                              {formatRate(quote.apr)}%
                              <Info
                                size={14}
                                className="text-gray-400 cursor-help"
                                data-tooltip-id="apr-tooltip-mobile"
                                data-tooltip-content="APR is a way to show the true cost of a loan over the full term of the loan (30 years). It combines interest rate and fees into one number."
                              />
                            </span>
                          </div>
                        )}
                      <div className="pt-2">
                        <button
                          onClick={() => handleSelectQuote(quote)}
                          className="w-full btn-secondary text-sm py-2"
                        >
                          Contact Broker
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-500 w-44 max-w-[16rem]">
                    Lender
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Rate
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Lender Fees
                    <Info
                      size={14}
                      className="text-gray-400 ml-1 inline-block cursor-help"
                      data-tooltip-id="lender-fees-tooltip"
                      data-tooltip-content="Title fees will be included as part of your closing costs, but are not shown in this comparison because they do not vary based on the lender or broker you choose."
                    />
                  </th>
                  {hasMortgageInsurance && (
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Mortgage Insurance
                    </th>
                  )}
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Monthly Payment
                    <Info
                      size={14}
                      className="text-gray-400 ml-1 inline-block cursor-help"
                      data-tooltip-id="monthly-payment-tooltip"
                      data-tooltip-content={MONTHLY_PAYMENT_TOOLTIP}
                    />
                  </th>
                  {showAprColumn && (
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      APR
                      <Info
                        size={14}
                        className="text-gray-400 ml-1 inline-block cursor-help"
                        data-tooltip-id="apr-tooltip"
                        data-tooltip-content="APR is a way to show the true cost of a loan over the full term of the loan (30 years). It combines interest rate and fees into one number."
                      />
                    </th>
                  )}
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {/* User Quote Group */}
                {userQuoteGroup.length > 0 && (
                  <tr className="bg-white">
                    <td className="align-top py-3 px-2 text-sm font-semibold text-gray-900 whitespace-normal break-words">
                      <div>
                        {userQuoteGroup[0].lenderName}
                        {quoteRequest.broker_rate && (
                          <div className="flex items-center mt-1">
                            <span className="text-xs text-gray-500">
                              Grade:
                            </span>
                            <span
                              className={`
                            ml-2 px-2 py-0.5 rounded-full text-white text-sm font-semibold
                            ${analysis.grade === "A" ? "bg-green-600" : ""}
                            ${
                              analysis.grade === "B" || analysis.grade === "C"
                                ? "bg-yellow-500"
                                : ""
                            }
                            ${
                              analysis.grade === "D" || analysis.grade === "F"
                                ? "bg-red-600"
                                : ""
                            }
                          `}
                            >
                              {analysis.grade}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-lg font-semibold text-gray-900">
                      {formatRate(userQuoteGroup[0].rate)}%
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {(() => {
                        const fees = quoteRequest.broker_lender_fees;
                        if (fees === null || fees === undefined) {
                          return (
                            <span className="text-gray-500">Not provided</span>
                          );
                        }
                        const numericFees =
                          typeof fees === "string" ? parseFloat(fees) : fees;
                        return numericFees < 0 ? (
                          <span className="text-green-500">
                            -${formatCurrency(Math.abs(numericFees))}
                          </span>
                        ) : (
                          <span>${formatCurrency(numericFees)}</span>
                        );
                      })()}
                    </td>
                    {hasMortgageInsurance && (
                      <td className="py-3 px-4 text-gray-700">
                        {(() => {
                          const pmi = quoteRequest.broker_mortgage_insurance;
                          const isPurchase =
                            quoteRequest.loan_type === "purchase";
                          const downPaymentPercent =
                            isPurchase &&
                            quoteRequest.purchase_price &&
                            quoteRequest.down_payment
                              ? (quoteRequest.down_payment /
                                  quoteRequest.purchase_price) *
                                100
                              : null;

                          if (pmi === null || pmi === undefined) {
                            if (
                              isPurchase &&
                              downPaymentPercent !== null &&
                              downPaymentPercent < 20
                            ) {
                              return (
                                <span className="text-gray-500">
                                  Not provided
                                </span>
                              );
                            }
                            return <span>$0</span>;
                          }

                          return <span>${formatCurrency(pmi)}</span>;
                        })()}
                      </td>
                    )}
                    <td className="py-3 px-4 text-gray-700">
                      $
                      {formatCurrency(
                        typeof userQuoteGroup[0].monthlyPayment === "number"
                          ? userQuoteGroup[0].monthlyPayment
                          : 0
                      )}
                    </td>
                    {showAprColumn && <td></td>}
                    <td></td>
                  </tr>
                )}

                {/* Partner Quote Group */}
                {partnerQuotesGroup.length > 0 && (
                  <>
                    <tr onMouseLeave={() => setHoveredRowIndex(null)}>
                      <td
                        rowSpan={partnerQuotesGroup.length}
                        className="summary-cell align-top py-3 px-2 text-sm font-semibold text-gray-900 whitespace-normal break-words bg-gray-50 w-44 max-w-[16rem]"
                      >
                        <div>
                          <span>Keystone Financial</span>
                          <br />
                          <span>Services LLC</span>
                          <div className="text-xs text-gray-500 mt-1">
                            NMLS #150818
                          </div>
                        </div>
                      </td>
                      <td
                        className={`py-3 px-4 text-lg font-semibold text-gray-900 cursor-pointer${
                          hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                        }`}
                        onMouseEnter={() => setHoveredRowIndex(0)}
                        onClick={() => {
                          setSelectedPartnerIndex(0);
                          setSelectedQuote(partnerQuotesGroup[0]);
                          setIsModalOpen(true);
                        }}
                      >
                        <span className="inline-flex items-center">
                          {formatRate(partnerQuotesGroup[0].rate)}%
                          {partnerQuotesGroup[0].isRecommended && (
                            <span className="ml-1">
                              <Star size={16} className="text-primary inline" />
                              <span className="sr-only">
                                Recommended Option
                              </span>
                            </span>
                          )}
                        </span>
                      </td>
                      <td
                        className={`py-3 px-4 text-gray-700 cursor-pointer${
                          hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                        }`}
                        onMouseEnter={() => setHoveredRowIndex(0)}
                        onClick={() => {
                          setSelectedPartnerIndex(0);
                          setSelectedQuote(partnerQuotesGroup[0]);
                          setIsModalOpen(true);
                        }}
                      >
                        {(() => {
                          const fees =
                            typeof partnerQuotesGroup[0].lenderFees === "string"
                              ? parseFloat(partnerQuotesGroup[0].lenderFees)
                              : partnerQuotesGroup[0].lenderFees;
                          return fees < 0 ? (
                            <span className="text-green-500">
                              -${formatCurrency(Math.abs(fees))}
                            </span>
                          ) : (
                            <span>${formatCurrency(fees)}</span>
                          );
                        })()}
                      </td>
                      {hasMortgageInsurance && (
                        <td
                          className={`py-3 px-4 text-gray-700 cursor-pointer${
                            hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(0)}
                          onClick={() => {
                            setSelectedPartnerIndex(0);
                            setSelectedQuote(partnerQuotesGroup[0]);
                            setIsModalOpen(true);
                          }}
                        >
                          $
                          {formatCurrency(
                            typeof partnerQuotesGroup[0].mortgageInsurance ===
                              "number"
                              ? partnerQuotesGroup[0].mortgageInsurance
                              : 0
                          )}
                        </td>
                      )}
                      <td
                        className={`py-3 px-4 text-gray-700 cursor-pointer${
                          hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                        }`}
                        onMouseEnter={() => setHoveredRowIndex(0)}
                        onClick={() => {
                          setSelectedPartnerIndex(0);
                          setSelectedQuote(partnerQuotesGroup[0]);
                          setIsModalOpen(true);
                        }}
                      >
                        $
                        {formatCurrency(
                          typeof partnerQuotesGroup[0].monthlyPayment ===
                            "number"
                            ? partnerQuotesGroup[0].monthlyPayment
                            : 0
                        )}
                      </td>
                      {showAprColumn && (
                        <td
                          className={`py-3 px-4 cursor-pointer${
                            hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(0)}
                          onClick={() => {
                            setSelectedPartnerIndex(0);
                            setSelectedQuote(partnerQuotesGroup[0]);
                            setIsModalOpen(true);
                          }}
                        >
                          {typeof partnerQuotesGroup[0].apr === "number" &&
                          !isNaN(partnerQuotesGroup[0].apr) ? (
                            <span className="text-lg font-semibold text-gray-900">
                              {partnerQuotesGroup[0].apr.toFixed(3)}%
                            </span>
                          ) : (
                            ""
                          )}
                        </td>
                      )}
                      <td
                        className={
                          hoveredRowIndex === 0 ? "bg-primary-light/10" : ""
                        }
                      ></td>
                    </tr>
                    {partnerQuotesGroup.slice(1).map((quote, index) => (
                      <tr
                        key={index}
                        onMouseLeave={() => setHoveredRowIndex(null)}
                      >
                        <td
                          className={`py-3 px-4 text-lg font-semibold text-gray-900 cursor-pointer${
                            hoveredRowIndex === index + 1
                              ? " bg-primary-light/10"
                              : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(index + 1)}
                          onClick={() => {
                            setSelectedPartnerIndex(index + 1);
                            setSelectedQuote(quote);
                            setIsModalOpen(true);
                          }}
                        >
                          <span className="inline-flex items-center">
                            {formatRate(quote.rate)}%
                            {quote.isRecommended && (
                              <span className="ml-1">
                                <Star
                                  size={16}
                                  className="text-primary inline"
                                />
                                <span className="sr-only">
                                  Recommended Option
                                </span>
                              </span>
                            )}
                          </span>
                        </td>
                        <td
                          className={`py-3 px-4 text-gray-700 cursor-pointer${
                            hoveredRowIndex === index + 1
                              ? " bg-primary-light/10"
                              : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(index + 1)}
                          onClick={() => {
                            setSelectedPartnerIndex(index + 1);
                            setSelectedQuote(quote);
                            setIsModalOpen(true);
                          }}
                        >
                          {(() => {
                            const fees =
                              typeof quote.lenderFees === "string"
                                ? parseFloat(quote.lenderFees)
                                : quote.lenderFees;
                            return fees < 0 ? (
                              <span className="text-green-500">
                                -${formatCurrency(Math.abs(fees))}
                              </span>
                            ) : (
                              <span>${formatCurrency(fees)}</span>
                            );
                          })()}
                        </td>
                        {hasMortgageInsurance && (
                          <td
                            className={`py-3 px-4 text-gray-700 cursor-pointer${
                              hoveredRowIndex === index + 1
                                ? " bg-primary-light/10"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredRowIndex(index + 1)}
                            onClick={() => {
                              setSelectedPartnerIndex(index + 1);
                              setSelectedQuote(quote);
                              setIsModalOpen(true);
                            }}
                          >
                            $
                            {formatCurrency(
                              typeof quote.mortgageInsurance === "number"
                                ? quote.mortgageInsurance
                                : 0
                            )}
                          </td>
                        )}
                        <td
                          className={`py-3 px-4 text-gray-700 cursor-pointer${
                            hoveredRowIndex === index + 1
                              ? " bg-primary-light/10"
                              : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(index + 1)}
                          onClick={() => {
                            setSelectedPartnerIndex(index + 1);
                            setSelectedQuote(quote);
                            setIsModalOpen(true);
                          }}
                        >
                          $
                          {formatCurrency(
                            typeof quote.monthlyPayment === "number"
                              ? quote.monthlyPayment
                              : 0
                          )}
                        </td>
                        {showAprColumn && (
                          <td
                            className={`py-3 px-4 cursor-pointer${
                              hoveredRowIndex === index + 1
                                ? " bg-primary-light/10"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredRowIndex(index + 1)}
                            onClick={() => {
                              setSelectedPartnerIndex(index + 1);
                              setSelectedQuote(quote);
                              setIsModalOpen(true);
                            }}
                          >
                            {typeof quote.apr === "number" &&
                            !isNaN(quote.apr) ? (
                              <span className="text-lg font-semibold text-gray-900">
                                {quote.apr.toFixed(3)}%
                              </span>
                            ) : (
                              ""
                            )}
                          </td>
                        )}
                        <td
                          className={
                            hoveredRowIndex === index + 1
                              ? "bg-primary-light/10"
                              : ""
                          }
                        ></td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
            <div className="flex justify-end mt-4">
              <button
                className="btn-primary px-6 py-2 text-base font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  openedByButtonRef.current = true;
                  if (selectedPartnerIndex !== null) {
                    setSelectedQuote(partnerQuotesGroup[selectedPartnerIndex]);
                  } else {
                    setSelectedQuote(null); // No row selected, open modal with dropdown
                  }
                  setIsModalOpen(true);
                }}
              >
                Contact Broker
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pros and Cons - Only show if there's a broker rate */}
      {hasProsAndCons && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              What's Good About Your Current Quote
            </h3>
            <ul className="space-y-2">
              {analysis.pros.map((pro, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <CheckCircle
                    size={20}
                    className="text-success shrink-0 mt-1"
                  />
                  <span className="text-gray-700">{pro}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Areas for Improvement
            </h3>
            <ul className="space-y-2">
              {analysis.cons.map((con, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <XCircle size={20} className="text-error shrink-0 mt-1" />
                  <span className="text-gray-700">{con}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Loan Terms Disclosure */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          <p className="font-medium mb-1 text-gray-600">
            Loan Terms & Finance Charges
          </p>
          <p>
            All quotes provided by Keystone Financial Services, LLC are for
            30-year conventional fixed-rate mortgages with 360 monthly payments.
            Total finance charges include lender fees and private mortgage
            insurance (PMI) if applicable. Monthly payments shown include
            principal, interest, and PMI only. Property taxes and homeowners
            insurance are not included.
          </p>
        </div>
      </div>

      <SpeakToProfessionalModal
        isOpen={isModalOpen}
        onClose={closeModal}
        quotes={partnerQuotesGroup}
        selectedQuote={selectedQuote}
        showDropdown={openedByButtonRef.current && selectedQuote === null}
        question={
          selectedQuote
            ? `I am interested in the ${selectedQuote.rate}% rate with ${
                selectedQuote.lenderFees < 0
                  ? `a credit of $${Math.abs(
                      selectedQuote.lenderFees
                    ).toLocaleString()} in lender fees`
                  : `$${selectedQuote.lenderFees.toLocaleString()} in lender fees`
              }.`
            : undefined
        }
        quoteRequestId={quoteRequest?.id}
      />
      <LoanInformationModal
        isOpen={isLoanInfoModalOpen}
        onClose={() => setIsLoanInfoModalOpen(false)}
        quoteRequest={quoteRequest}
      />
      <Tooltip id="monthly-payment-tooltip" className="max-w-sm" />
      <Tooltip id="lender-fees-tooltip" className="max-w-sm" />
      <Tooltip id="apr-tooltip" className="max-w-sm" />
      <Tooltip id="monthly-payment-tooltip-mobile" className="max-w-sm" />
      <Tooltip id="lender-fees-tooltip-mobile" className="max-w-sm" />
      <Tooltip id="apr-tooltip-mobile" className="max-w-sm" />
    </div>
  );
};

export default QuoteComparisonReport;
